import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { api } from '@/api/client';

interface Props { cityId?: number; defaultValues?: any; onSuccess: () => void }
interface State { id: number; name: string }

export function CityForm({ cityId, defaultValues, onSuccess }: Props) {
  const qc = useQueryClient();
  const isEdit = !!cityId;
  const { data: states } = useQuery({ queryKey: ['states'], queryFn: async () => (await api.get<State[]>('/states')).data });
  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: { isActive: true, isMetro: false, ...defaultValues, stateId: defaultValues?.stateId ?? '' },
  });
  const save = useMutation({
    mutationFn: async (d: any) => {
      const payload: any = { ...d };
      if (payload.stateId === '' || payload.stateId == null) delete payload.stateId; else payload.stateId = Number(payload.stateId);
      return isEdit
        ? (await api.patch(`/cities/${cityId}`, payload)).data
        : (await api.post('/cities', payload)).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['cities'] }); toast.success(isEdit ? 'Updated' : 'Created'); onSuccess(); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Save failed'),
  });
  return (
    <form onSubmit={handleSubmit((d) => save.mutate(d))} className="space-y-3">
      <Field label="City Name *" error={(errors as any).name?.message}>
        <input {...register('name', { required: 'Required' })} className="input" />
      </Field>
      <Field label="State">
        <select {...register('stateId')} className="input">
          <option value="">— Select state —</option>
          {states?.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </Field>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" {...register('isMetro')} className="rounded" /> Metro city
      </label>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" {...register('isActive')} className="rounded" /> Active
      </label>
      <div className="flex justify-end gap-2 pt-2 border-t">
        <button type="button" onClick={onSuccess} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
        <button type="submit" disabled={save.isPending} className="px-4 py-2 bg-primary text-white text-sm rounded-lg disabled:opacity-60 flex items-center gap-2">
          {save.isPending && <Loader2 size={14} className="animate-spin" />}
          {isEdit ? 'Save' : 'Create'}
        </button>
      </div>
    </form>
  );
}

function Field({ label, children, error }: { label: string; children: React.ReactNode; error?: string }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {children}
      {error && <p className="text-danger text-xs mt-1">{error}</p>}
    </div>
  );
}
